import React, { useState } from "react";
import { AlertCircle, Clock, Package, Save, ShieldCheck } from "lucide-react";
import { useReminderSettings } from "../hooks/useReminderSettings";
import api from "../../../services/api";
import { toast } from "react-hot-toast";

export default function ReminderSettingsTab() {
  const [licenseReminderEnabled, setLicenseReminderEnabled] = useState(false);
  const [isSendingLicense, setIsSendingLicense] = useState(false);

  const handleSendLicenseReminder = async () => {
    setIsSendingLicense(true);
    try {
      const promise = api.post('/reminders/send-license-reminder');
      await toast.promise(promise, {
        loading: "Sending license renewal reminder...",
        success: "Reminder sent successfully to system administrator!",
        error: "Failed to send reminder",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSendingLicense(false);
    }
  };

  const {
    reminders,
    setReminders,
    loading,
    saving,
    toggleActive,
    updateDays,
    inventoryItems,
    selectedItemId,
    setSelectedItemId,
    selectedItemUnit,
    lowStockThreshold,
    setLowStockThreshold,
    isItemActive,
    setIsItemActive,
    saveInventoryThreshold,
    isInventorySaving,
  } = useReminderSettings();

  if (loading) {
    return <div className="p-10 text-center animate-pulse text-slate-400">Loading settings...</div>;
  }

  return (
    <div className="max-w-4xl space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="mb-6">
        <h3 className="text-lg font-bold text-slate-800">Automated Reminders</h3>
        <p className="text-sm text-slate-500">
          Configure when the system should notify your team about lead follow-ups and task deadlines.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {reminders.map((reminder) => (
          <div
            key={reminder._id}
            className={`card p-4 sm:p-5 border border-slate-200 hover:border-brand-blue/50 transition-all group overflow-hidden`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 sm:gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div
                  className={`w-12 h-12 rounded-xl shrink-0 flex items-center justify-center transition-all ${
                    reminder.active
                      ? "bg-brand-blue/10 text-brand-blue shadow-sm shadow-blue-100"
                      : "bg-slate-100 text-slate-400"
                  }`}
                >
                  <Clock size={20} strokeWidth={3} />
                </div>
                <div className="min-w-0">
                  <h4 className="font-bold text-slate-800 text-sm sm:text-base tracking-tight leading-none mb-1 truncate">
                    {reminder.title}
                  </h4>
                  <div className="flex flex-col gap-1.5 mt-2">
                    <p className="text-[10px] sm:text-[11px] text-slate-400 font-medium">
                      Triggered if contact is not updated
                    </p>
                    <span
                      className={`w-fit px-2 py-0.5 rounded-lg text-[9px] font-bold tracking-wide capitalize ${
                        reminder.type === "contact_later"
                          ? "bg-amber-100 text-amber-700 border border-amber-200"
                          : "bg-blue-100 text-blue-700 border border-blue-200"
                      }`}
                    >
                      {reminder.type === "contact_later" ? "All Active Contacts" : String(reminder.type || "").replace("_", " ")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between sm:justify-end gap-3 sm:gap-4 bg-slate-50 p-2 sm:p-2.5 rounded-2xl border border-slate-100/50 w-full sm:w-auto">
                <div className="flex items-center gap-2 sm:gap-3 px-1 sm:px-3 whitespace-nowrap">
                  <span className="text-[10px] sm:text-xs font-semibold text-slate-500 tracking-wide">
                    Notify after
                  </span>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <input
                      type="number"
                      min="1"
                      value={reminder.days}
                      onChange={(e) =>
                        setReminders((prev) =>
                          prev.map((entry) =>
                            entry._id === reminder._id
                              ? { ...entry, days: e.target.value }
                              : entry,
                          ),
                        )
                      }
                      className="w-10 sm:w-14 px-1 sm:px-2 py-1 bg-white border border-slate-200 rounded-lg text-sm font-black text-slate-800 text-center outline-none focus:ring-2 focus:ring-brand-blue/20 transition-all"
                    />
                    <span className="text-[10px] sm:text-xs font-semibold text-slate-500">days</span>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 px-1 ml-auto sm:ml-0">
                  <button
                    onClick={() => updateDays(reminder._id, reminder.days)}
                    disabled={saving}
                    className="p-1.5 sm:p-2 bg-slate-200 text-slate-600 rounded-lg sm:rounded-xl hover:bg-blue-600 hover:text-white active:scale-95 transition-all shadow-sm shrink-0 disabled:opacity-50"
                    title="Save Changes"
                  >
                    <Save size={15} strokeWidth={2.5} />
                  </button>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={reminder.active}
                      onChange={() => toggleActive(reminder)}
                      className="sr-only peer"
                    />
                    <div className="w-[36px] sm:w-[42px] h-5 sm:h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-brand-blue/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-brand-blue after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 sm:after:h-5 after:w-4 sm:after:w-5 after:transition-all shadow-inner"></div>
                  </label>
                </div>
              </div>
            </div>

            {reminder.active && (
              <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-700 bg-neutral-50 px-3 py-2 rounded-lg border border-slate-100">
                <AlertCircle size={14} className="text-brand-blue/60" />
                <span>
                  Staff will be notified on their dashboard {reminder.days} day
                  {reminder.days > 1 ? "s" : ""}.
                </span>
              </div>
            )}
          </div>
          /* ) */
        ))}
      </div>

      {/* <div className="mt-10 pt-10 border-t border-slate-100">
        <div className="mb-6">
          <h3 className="text-lg font-bold text-slate-800">Inventory Low Stock Alerts</h3>
          <p className="text-sm text-slate-500">
            Configure stock thresholds to receive alerts when items are running low.
          </p>
        </div>

        <div className="card p-5 border border-slate-200 hover:border-brand-blue/50 transition-all group overflow-hidden bg-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-colors bg-gray-800/10 text-gray-800">
                <Package size={22} strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-slate-800 text-md tracking-tight leading-none mb-1">
                  Stock Level Reminders
                </h4>
                <div className="mt-3">
                  <select
                    value={selectedItemId}
                    onChange={(e) => setSelectedItemId(e.target.value)}
                    className="w-full max-w-md px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold text-slate-700 outline-none focus:border-brand-blue transition-all"
                  >
                    <option value="">Select an Item to configure...</option>
                    {inventoryItems.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.itemName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 sm:gap-4 bg-slate-50/80 p-2 sm:p-3 rounded-2xl border border-slate-100 flex-1 min-w-[280px]">
              <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 flex-1 min-w-fit">
                <span className="text-xs font-extrabold text-slate-500 uppercase tracking-tighter shrink-0">
                  Threshold
                </span>
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={lowStockThreshold}
                  onChange={(e) => setLowStockThreshold(e.target.value)}
                  className="w-16 sm:w-20 px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-sm font-black text-slate-800 text-center outline-none focus:border-brand-blue transition-all"
                />
                <span className="text-xs font-bold text-slate-400 shrink-0">{selectedItemUnit}</span>
              </div>

              <div className="flex items-center gap-4 px-2 ml-auto sm:ml-0">
                <button
                  onClick={saveInventoryThreshold}
                  disabled={isInventorySaving || !selectedItemId}
                  className="px-4 py-2 sm:p-2 bg-gray-800 text-white rounded-lg sm:rounded-xl hover:scale-105 active:scale-95 transition-all shadow-md shadow-orange-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-xs font-bold uppercase"
                  title="Save Low Stock Threshold"
                >
                  <Save size={16} strokeWidth={2.5} />
                  <span className="sm:hidden">Save</span>
                </button>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isItemActive}
                    onChange={() => setIsItemActive((prev) => !prev)}
                    className="sr-only peer"
                    disabled={!selectedItemId}
                  />
                  <div className="w-[40px] h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-brand-blue/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-gray-800 after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4.5 after:w-4.5 after:transition-all shadow-inner"></div>
                </label>
              </div>
            </div>
          </div>

          {selectedItemId && isItemActive && (
            <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-700 bg-neutral-50 px-3 py-2 rounded-lg border border-slate-100 animate-in fade-in slide-in-from-top-1">
              <AlertCircle size={14} className="text-gray-800/60" />
              <span>
                System will show this item as "low stock" in the dashboard when available quantity is{" "}
                {lowStockThreshold || 0} or less.
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 pt-10 border-t border-slate-100">
        <div className="mb-6">
          <h3 className="text-lg font-bold text-slate-800">System License Reminders</h3>
          <p className="text-sm text-slate-500">
            Configure automated alerts before your enterprise software license expires.
          </p>
        </div>

        <div className="card p-5 border border-slate-200 hover:border-emerald-500/50 transition-all group overflow-hidden bg-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-colors bg-emerald-50 text-emerald-600">
                <ShieldCheck size={22} strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-slate-800 text-md tracking-tight leading-none mb-1">
                  Subscription Renewal Alert
                </h4>
                <div className="mt-2 text-sm text-slate-500">
                  Notify system administrators 30 days prior to license expiration.
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 px-2 ml-auto sm:ml-0 bg-slate-50/80 p-2 sm:p-3 rounded-2xl border border-slate-100 min-w-fit">
              <button
                onClick={handleSendLicenseReminder}
                disabled={isSendingLicense}
                className="px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 active:scale-95 transition-all shadow-md shadow-emerald-100 disabled:opacity-50 text-xs font-bold"
              >
                {isSendingLicense ? "Sending..." : "Send Test Alert"}
              </button>
              <label className="relative inline-flex items-center cursor-pointer ml-2">
                <input
                  type="checkbox"
                  checked={licenseReminderEnabled}
                  onChange={() => setLicenseReminderEnabled(!licenseReminderEnabled)}
                  className="sr-only peer"
                />
                <div className="w-[40px] h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-emerald-500/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-emerald-500 after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4.5 after:w-4.5 after:transition-all shadow-inner"></div>
              </label>
            </div>
          </div>

          {licenseReminderEnabled && (
            <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-700 bg-neutral-50 px-3 py-2 rounded-lg border border-slate-100 animate-in fade-in slide-in-from-top-1">
              <AlertCircle size={14} className="text-emerald-500/60" />
              <span>
                System administrators will automatically receive a renewal reminder 30 days before expiration.
              </span>
            </div>
          )}
        </div>
      </div> */}
    </div>
  );
}




